import type { IconProp } from "@fortawesome/fontawesome-svg-core";
import type { ButtonProps } from "@repo/ui";
import type { Dialog as DialogPrimitive } from "radix-ui";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { cn } from "@repo/helpers";
import { Button } from "@repo/ui";
import * as React from "react";

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../base";

interface BasicDialogProps extends React.ComponentProps<typeof DialogPrimitive.Root> {
	title: string;
	triggerText?: string | React.ReactNode;
	triggerIcon?: IconProp;
	triggerProps?: ButtonProps;
	hideTrigger?: boolean;
	fullWidth?: boolean;
	/**
	 * If true, the dialog content will have `overflow: visible` instead of `overflow: auto`.
	 * Useful for pickers/menus that need to overflow the dialog.
	 **/
	allowOverflow?: boolean;
	className?: string;
	onClose?: () => void;
}
function BasicDialog({
	title,
	triggerText,
	triggerIcon,
	triggerProps,
	hideTrigger = false,
	fullWidth = true,
	allowOverflow = false,
	onClose,
	className,
	children,
	...props
}: BasicDialogProps) {
	return (
		<Dialog {...props}>
			{!hideTrigger && (
				<DialogTrigger onClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()} asChild>
					<Button {...triggerProps}>
						{triggerIcon && <FontAwesomeIcon icon={triggerIcon} />} {triggerText}
					</Button>
				</DialogTrigger>
			)}
			<DialogContent
				className={cn("!p-0", fullWidth && "!w-10/12 !max-w-none", className)}
				showCloseButton={!hideTrigger}
				onClose={onClose}
			>
				<DialogHeader className="rounded-t-xl bg-gray-300 p-4">
					<DialogTitle className="my-1">{title}</DialogTitle>
				</DialogHeader>
				<div className={cn("px-4 pb-4", allowOverflow ? "overflow-visible" : "overflow-auto")}>{children}</div>
			</DialogContent>
		</Dialog>
	);
}

export { BasicDialog };
